<script setup lang="ts">
import { computed } from 'vue'

import { EMPTY_CELL } from '@/shared/constants'
import { Tag, type TagType } from '@/shared/ui/Tag'

/**
 * A state rendered as a coloured word: `labels` says what to call each value, `types` what
 * colour it gets. **Never write a per-entity `<X>StatusTag.vue`** — the two maps belong in
 * that entity's `model/constants.ts` and the rendering belongs here.
 *
 * Keys are matched case-insensitively — an API that types a state as a plain string
 * may spell it either way — and a value the maps do not know is shown raw rather than
 * prettified, so a new backend state reads as something to map, not as a supported one.
 */
const props = withDefaults(
  defineProps<{
    value?: string | number | boolean | null
    labels: Record<string, string>
    types?: Record<string, TagType>
    /** The colour of a value `types` does not name. */
    fallbackType?: TagType
    size?: 'large' | 'default' | 'small'
    /**
     * The API's own wording for this row, shown instead of `labels` when it is set —
     * the colour still comes from `value`. An empty string falls back to `labels`.
     */
    label?: string | null
    /** Let a long label break onto several lines instead of overflowing its column. */
    wrap?: boolean
  }>(),
  {
    value: null,
    types: () => ({}),
    fallbackType: 'info',
    size: undefined,
    label: null,
    wrap: false
  }
)

const key = computed(() => String(props.value ?? '').toLowerCase())

const text = computed(
  () =>
    props.label ||
    (props.labels[key.value] ?? (key.value ? String(props.value) : EMPTY_CELL))
)

const type = computed(() => props.types[key.value] ?? props.fallbackType)
</script>

<template>
  <Tag :type="type" :size="size" :wrap="wrap">{{ text }}</Tag>
</template>
